import React, { useMemo } from 'react'
import moment from 'moment'
import './AlertNotificationStack.css'

const IcoSpeed = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M12 3 L2.5 20 H21.5 Z" stroke="white" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
    <line x1="12" y1="10" x2="12" y2="15" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="12" cy="18.2" r="1.1" fill="white"/>
  </svg>
)

const IcoZone = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="white" strokeWidth="2"/>
    <path d="M3 9h18M9 3v18" stroke="white" strokeWidth="1.5" strokeDasharray="3 2"/>
    <path d="M12 12l3 3m0-3l-3 3" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
)

const IcoTow = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M9.5 14.5a5 5 0 0 0 6.36.36l3-3a5 5 0 0 0-7.07-7.07L10 6.5" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.5 9.5a5 5 0 0 0-6.36-.36l-3 3a5 5 0 0 0 7.07 7.07L14 17.5" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="4.5" y1="4.5" x2="19.5" y2="19.5" stroke="white" strokeWidth="1.9" strokeLinecap="round"/>
  </svg>
)

const TYPE_CONFIG = {
  overspeed: { Icon: IcoSpeed },
  zone:      { Icon: IcoZone },
  towing:    { Icon: IcoTow },
}

function AlertCard({ type, label, subLabel, startTs, endTs, currentTs, onClick }) {
  const isActive = currentTs >= startTs && currentTs <= endTs
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.overspeed
  const Icon = cfg.Icon

  return (
    <div
      className={'alert-noti-card alert-noti-card--' + type + (isActive ? ' alert-noti-card--active' : '')}
      onClick={onClick}
      title="Click to jump to this alert (−2 min)"
    >
      <div className="alert-noti-icon">
        <Icon />
      </div>
      <div className="alert-noti-body">
        <div className="alert-noti-label">{label}</div>
        {subLabel && <div className="alert-noti-sub">{subLabel}</div>}
        <div className="alert-noti-time">
          {moment(startTs).format('HH:mm:ss')} – {moment(endTs).format('HH:mm:ss')}
        </div>
      </div>
      {!isActive && <div className="alert-noti-overlay" />}
    </div>
  )
}

export default function AlertNotificationStack({
  visible,
  currentTs,
  overspeedEvents,
  unauthorizedZoneEvents,
  towingEvents,
  bleAssetsMap,
  onAlertClick,
}) {
  const alerts = useMemo(() => {
    if (!visible) return []
    const list = []

    ;(overspeedEvents || []).forEach((ev, i) => {
      const startTs = Number(ev.start_timestamp || 0)
      const endTs   = Number(ev.end_timestamp   || 0)
      if (!startTs || !endTs) return
      const sub = ev.speed_limit != null ? ('Limit: ' + ev.speed_limit + ' km/h') : null
      list.push({ id: 'os_' + i, type: 'overspeed', label: 'Overspeed', subLabel: sub, startTs, endTs })
    })

    ;(unauthorizedZoneEvents || []).forEach((ev, i) => {
      const startTs = Number(ev.start_timestamp || 0)
      const endTs   = Number(ev.end_timestamp   || 0)
      if (!startTs || !endTs) return
      list.push({ id: 'zone_' + i, type: 'zone', label: 'Restricted Zone', subLabel: null, startTs, endTs })
    })

    ;(towingEvents || []).filter(ev => ev.status === 'DISCONNECTED' && !ev.synthetic).forEach((ev, i) => {
      const eventTs = Number(ev.eventTs || 0)
      if (!eventTs) return
      const mac = ev.instanceId || ''
      const asset = bleAssetsMap && bleAssetsMap[mac]
      const sub = asset
        ? ([asset.assetCode, asset.ownerCode].filter(Boolean).join(' / ') || mac)
        : (mac || null)
      // Active window: from eventTs for 2 minutes, matching the tooltip disconnect flash logic
      list.push({ id: 'tow_' + i, type: 'towing', label: 'Towing Disconnected', subLabel: sub, startTs: eventTs, endTs: eventTs + 2 * 60 * 1000 })
    })

    return list.sort((a, b) => b.startTs - a.startTs)
  }, [visible, overspeedEvents, unauthorizedZoneEvents, towingEvents, bleAssetsMap])

  if (!visible || !alerts.length) return null

  return (
    <div className="alert-noti-stack" onWheel={e => e.stopPropagation()}>
      {alerts.map(alert => (
        <AlertCard
          key={alert.id}
          {...alert}
          currentTs={currentTs}
          onClick={() => onAlertClick && onAlertClick(alert)}
        />
      ))}
    </div>
  )
}
